// biome-ignore-all lint/plugin/no-unlabeled-form-inputs: legacy unlabeled inputs, burn-down tracked in HTUI-392
import {
  BadgeButton,
  BadgeGroup,
  Box,
  Button,
  Checkbox,
  CloseIcon,
  Column,
  Dialog,
  IconButton,
  PlusIcon,
  Row,
  Select,
  Text,
  TextInput,
  Tooltip,
  useDisclosure,
} from "@hightouch/htui";
import { useState } from "react";
import { useFormContext } from "react-hook-form";

import type { ImputationConfig } from "@hightouch/lib/customer-data/decision-engine/types";
import type { FeatureMatrixFormValues } from "src/pages/decision-engines/configuration/feature-matrices/types";
import {
  SHOULD_DIRTY_OPTION,
  isImputationEligible,
} from "src/pages/decision-engines/configuration/feature-matrices/utils";
import { TextWithTooltip } from "src/components/text-with-tooltip";

const STRATEGY_OPTIONS = [
  { label: "Mean", value: "mean" },
  { label: "Median", value: "median" },
  { label: "Most frequent", value: "most_frequent" },
  { label: "Constant", value: "constant" },
];

function getStrategyLabel(config: ImputationConfig | null): string {
  if (!config) return "";
  const option = STRATEGY_OPTIONS.find((o) => o.value === config.strategy);
  if (!option) return "";
  if (config.strategy === "constant" && config.fill_value != null) {
    return `${option.label}: ${config.fill_value}`;
  }
  return option.label;
}

interface CopyableConfig {
  config: ImputationConfig;
  sourceColumns: string[];
}

function getCopyableConfigs(
  allModelColumns:
    | Record<string, { imputation?: ImputationConfig | null }>
    | undefined,
  columnName: string,
  currentImputation: ImputationConfig | null,
): CopyableConfig[] {
  const result: CopyableConfig[] = [];

  for (const [name, colConfig] of Object.entries(allModelColumns ?? {})) {
    if (name === columnName || !colConfig.imputation) continue;
    const key = JSON.stringify(colConfig.imputation);

    if (currentImputation && JSON.stringify(currentImputation) === key)
      continue;

    const existing = result.find((r) => JSON.stringify(r.config) === key);
    if (existing) {
      if (!existing.sourceColumns.includes(name)) {
        existing.sourceColumns.push(name);
      }
    } else {
      result.push({ config: colConfig.imputation, sourceColumns: [name] });
    }
  }
  return result;
}

export function ImputationCell({
  modelKey,
  columnName,
  colType,
  isDisabled,
  requiresAggregation,
}: {
  modelKey: string;
  columnName: string;
  colType: string;
  isDisabled: boolean;
  requiresAggregation: boolean;
}) {
  const { watch, setValue } = useFormContext<FeatureMatrixFormValues>();
  const config = watch(`relationships.${modelKey}.${columnName}`);
  const allModelColumns = watch(`relationships.${modelKey}`);
  const imputation = config?.imputation ?? null;
  const aggregates = config?.aggregates ?? [];
  const castTo = config?.castTo ?? null;

  const eligible = isImputationEligible(
    colType,
    aggregates,
    requiresAggregation,
    castTo,
  );

  const editDialog = useDisclosure();
  const [editingConfig, setEditingConfig] = useState<ImputationConfig | null>(
    null,
  );

  if (isDisabled) {
    return (
      <Box minWidth={0} width="100%">
        <Text size="sm" color="text.tertiary">
          —
        </Text>
      </Box>
    );
  }

  if (!eligible) {
    return (
      <Box minWidth={0} width="100%">
        <Tooltip message="Null handling is only supported for numeric columns and aggregations yielding numeric values (ex: count)">
          <Text size="sm" color="text.tertiary">
            —
          </Text>
        </Tooltip>
      </Box>
    );
  }

  const setImputation = (value: ImputationConfig | null) => {
    setValue(
      `relationships.${modelKey}.${columnName}.imputation`,
      value,
      SHOULD_DIRTY_OPTION,
    );
  };

  const handleEdit = () => {
    setEditingConfig(imputation);
    editDialog.onOpen();
  };

  const handleNew = () => {
    setEditingConfig(null);
    editDialog.onOpen();
  };

  const handleSave = (saved: ImputationConfig) => {
    setImputation(saved);
    editDialog.onClose();
    setEditingConfig(null);
  };

  const label = getStrategyLabel(imputation);

  return (
    <>
      <Row gap={1} align="center" minWidth={0} overflow="hidden">
        {imputation && (
          <Box as={BadgeGroup} minWidth={0} overflow="hidden">
            <Box
              as={BadgeButton}
              rightActionProps={{
                icon: CloseIcon,
                "aria-label": "remove",
                onClick: (e: React.MouseEvent) => {
                  e.stopPropagation();
                  setImputation(null);
                },
              }}
              onClick={handleEdit}
              maxWidth="2xs"
            >
              <TextWithTooltip size="sm">{label}</TextWithTooltip>
            </Box>
          </Box>
        )}
        {!imputation && (
          <IconButton
            icon={PlusIcon}
            aria-label="Add null handling"
            size="sm"
            variant="secondary"
            onClick={handleNew}
          />
        )}
      </Row>

      {editDialog.isOpen && (
        <ImputationDialog
          config={editingConfig}
          copyableConfigs={getCopyableConfigs(
            allModelColumns,
            columnName,
            imputation,
          )}
          onSave={handleSave}
          onClose={() => {
            editDialog.onClose();
            setEditingConfig(null);
          }}
        />
      )}
    </>
  );
}

function ImputationDialog({
  config,
  copyableConfigs,
  onSave,
  onClose,
}: {
  config: ImputationConfig | null;
  copyableConfigs: CopyableConfig[];
  onSave: (config: ImputationConfig) => void;
  onClose: () => void;
}) {
  const [strategy, setStrategy] = useState<string>(config?.strategy ?? "");
  const [fillValue, setFillValue] = useState<string>(
    String(config?.fill_value ?? ""),
  );
  const [addIndicator, setAddIndicator] = useState<boolean>(
    config?.add_indicator ?? true,
  );

  const isSaveDisabled =
    !strategy || (strategy === "constant" && fillValue.trim() === "");

  const handleSave = () => {
    if (isSaveDisabled) return;
    const result: ImputationConfig = {
      strategy: strategy as ImputationConfig["strategy"],
      add_indicator: addIndicator,
    };
    if (strategy === "constant") {
      const parsed = parseFloat(fillValue);
      result.fill_value = isNaN(parsed) ? 0 : parsed;
    }
    onSave(result);
  };

  const handleApplyCopyable = (copied: ImputationConfig) => {
    setStrategy(copied.strategy);
    setFillValue(String(copied.fill_value ?? ""));
    setAddIndicator(copied.add_indicator ?? true);
  };

  return (
    <Dialog
      isOpen
      title={config ? "Edit null handling" : "Add null handling"}
      variant="form"
      width="md"
      actions={
        <Row gap={2}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            isDisabled={isSaveDisabled}
            onClick={handleSave}
          >
            Save
          </Button>
        </Row>
      }
      onClose={onClose}
    >
      <Column gap={4}>
        {!config && copyableConfigs.length > 0 && (
          <Column gap={1}>
            <Text size="sm" fontWeight="medium">
              Copy from another column
            </Text>
            <Row gap={2} flexWrap="wrap">
              {copyableConfigs.map(({ config: copyConfig, sourceColumns }) => (
                <Button
                  key={JSON.stringify(copyConfig)}
                  size="sm"
                  variant="secondary"
                  onClick={() => handleApplyCopyable(copyConfig)}
                >
                  {getStrategyLabel(copyConfig)} (
                  {sourceColumns.slice(0, 2).join(", ")}
                  {sourceColumns.length > 2 ? "..." : ""})
                </Button>
              ))}
            </Row>
          </Column>
        )}
        <Column gap={1}>
          <Text size="sm" fontWeight="medium">
            Strategy
          </Text>
          <Select
            options={STRATEGY_OPTIONS}
            value={strategy}
            onChange={(value) => setStrategy(value ?? "")}
            placeholder="Select strategy"
          />
        </Column>
        {strategy === "constant" && (
          <Column gap={1}>
            <Text size="sm" fontWeight="medium">
              Fill value
            </Text>
            <TextInput
              type="number"
              width="100%"
              value={fillValue}
              onChange={(e) => setFillValue(e.target.value)}
            />
          </Column>
        )}
        <Row gap={2} align="center">
          <Checkbox
            isChecked={addIndicator}
            onChange={(e) => setAddIndicator(e.target.checked)}
            aria-label="Add missing indicator"
          />
          <Tooltip message="Adds a binary feature indicating whether the original value was missing">
            <Text size="sm">Add missing indicator</Text>
          </Tooltip>
        </Row>
      </Column>
    </Dialog>
  );
}
